import { Model } from "@bryntum/schedulerpro";
import { HasProjectMixin } from "./mixins/HasProjectMixin.jsx";
import { getAging } from "../../other/utils/utils.jsx";
import { STORE_KEYS } from "../../other/constants/StoreKeys.jsx";
import { INCENTIVE_POINT_STATUS_VALUES, INCENTIVE_TYPES_VALUES } from "@/other/constants/IncentiveKeys.jsx";

export class IncentivePoint extends HasProjectMixin(Model) {
  static get $name() {
    return "IncentivePoint";
  }

  static get relations() {
    return {
      taskRecord: {
        foreignKey: "taskId",
        foreignStore: STORE_KEYS.taskStore,
        relatedCollectionName: "taskRecord",
        propagateRecordChanges: true
      },
      pointTagRecord: {
        foreignKey: "tagId",
        foreignStore: STORE_KEYS.incentivePointTagsStore,
        relatedCollectionName: "tagRecord",
        propagateRecordChanges: true
      },
    }
  }

  static get fields() {
    return [
      { name: "outcomeIncentiveId", dataSource: "outcome_incentive" },
      { name: "spotIncentiveId", dataSource: "spot_incentive" },
      { name: "taskId", dataSource: "task" },
      { name: "taskName", dataSource: "task_name" }, // Only populates once the task is deleted
      { name: "incentiveName", dataSource: "incentive_name" },
      { name: "incentiveType", dataSource: "incentive_type" },
      { name: "boardUserId", dataSource: "board_user" },
      { name: "points", dataSource: "points", type: "number" },
      { name: "status",  dataSource: "status" },
      { name: "dateEarned",  dataSource: "date_earned", type: "date" },
      { name: "statusChangedBy", dataSource: "status_changed_by" },
      { name: "statusChangedAt", dataSource: "status_changed_at", type: "date" },
      { name: "tagId", dataSource: "tag" },
      { name: "creatorId", dataSource: "creator" },
      { name: "updaterId", dataSource: "updater" },
      { name: "createdAt", dataSource: "created_at", type: "date" },
      { name: "updatedAt", dataSource: "updated_at", type: "date" },
      { name: "archived", dataSource: "archived", type: "boolean" },
      { name: "tagName", dataSource: "pointTagRecord.name", persist: false },
    ];
  }

  static swivuGetFieldOriginalSourceName(fieldName) {
    const fieldsMaps = {
      taskName: "task",
      incentiveType: "incentive_type",
      incentiveStatus: "status",
      incentiveName: "incentive_name",
      boardUserName: "board_user",
      points: "points",
      "pointTagRecord.name": "tag"
    }
    return fieldsMaps[fieldName] || super.swivuGetFieldOriginalSourceName?.(fieldName);
  }

  get taskName() {
    return this.taskRecord?.name || this.get("taskName"); // fallback to task_name if task is deleted
  }

  get aging() {
    return getAging(this.get("createdAt"));
  }

  get incentive() {
    if (this.project) {
      if (this.get("outcomeIncentiveId")) {
        return this.project.getCrudStore(STORE_KEYS.outcomeIncentiveStore).getById(this.outcomeIncentiveId);
      } else if (this.get("spotIncentiveId")) {
        return this.project.getCrudStore(STORE_KEYS.spotIncentiveStore).getById(this.spotIncentiveId);
      }
    }
    return null;
  }

  get entityVisibleName() {
    return `Point: ${this.incentiveName}`;
  }

  get incentiveName() {
    return this.get("incentiveName") || this.incentive?.name;
  }

  get incentiveType() {
    return INCENTIVE_TYPES_VALUES[this.get("incentiveType")] || this.incentive?.incentiveType;
  }

  get incentiveTypeValue() {
    return this.get("incentiveType");
  }

  get incentiveStatus() {
    return INCENTIVE_POINT_STATUS_VALUES[this.status];
  }

  get boardUser() {
    return this?.project?.getCrudStore?.(STORE_KEYS.boardUserStore)?.getById(this.boardUserId);
  }

  get boardUserName() {
    return this.boardUser?.name;
  }

  get statusChangedByUser() {
    return this?.project?.getCrudStore?.(STORE_KEYS.boardUserStore)?.getById(this.statusChangedBy);
  }
}
